import type { ImageLoader } from 'next/image';
import Image from 'next/image';
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type { OrderDetailsOrderLine } from '@/types/orderDetailsOrder';
import { getOrderLineUnitPriceWithoutDiscount } from '@/utils/order';

import { Box } from '@/components/Box';
import { Checkbox } from '@/components/Checkbox';
import { Flex } from '@/components/Flex';
import { getWarrantyOrderLineDetails } from '@/components/MyOrders/OrderCard/utils';
import { Select, type SelectOption } from '@/components/Select';
import { TaxedPrice } from '@/components/TaxedPrice';
import { TextArea } from '@/components/TextArea';
import { Typography } from '@/components/Typography';
import { ProductType, VOUCHER_TYPE } from '@/lib/const';
import { getProductType } from '@/lib/product';
import { withdrawalDisabledReasonLabel } from '@/lib/withdrawal/disabledReasonLabels';
import * as PM from '@/messages/payment';
import { ProductName } from '@/views/OrderDetailsView/OrderDetailsView';
import * as S from '@/views/OrderDetailsView/OrderDetailsView.styles';
import type { ReturnLineItemViewModel } from '@/views/OrderReturnView/orderReturnTypes';
import * as RowStyles from '@/views/OrderReturnView/OrderReturnView.styles';

function getSelectedReasonOption(
  reason: string,
  reasonOptions: SelectOption[],
): SelectOption | null {
  return reasonOptions.find((o) => o.value === reason) ?? null;
}

function getSelectOptionValue(opt: unknown): string {
  const value = (opt as SelectOption | null)?.value;
  if (value == null) return '';
  return String(value);
}

function shouldShowIneligibleCaption({
  eligibilityLineDisabledReasonCode,
  isEligible,
  isKnown,
  isWithdrawalEligibilityLoading,
}: Readonly<{
  eligibilityLineDisabledReasonCode: string | null | undefined;
  isEligible: boolean;
  isKnown: boolean;
  isWithdrawalEligibilityLoading: boolean;
}>) {
  if (isWithdrawalEligibilityLoading) return false;
  if (eligibilityLineDisabledReasonCode === 'OUT_OF_EU_SCOPE') return false;
  if (eligibilityLineDisabledReasonCode === 'INVALID_LINE_STATUS') return false;
  return !isKnown || !isEligible;
}

function buildIneligibleCaption({
  eligibilityLineDisabledReasonCode,
  formatMessage,
  isEligible,
  isKnown,
  isWithdrawalEligibilityLoading,
}: Readonly<{
  eligibilityLineDisabledReasonCode: string | null | undefined;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  isEligible: boolean;
  isKnown: boolean;
  isWithdrawalEligibilityLoading: boolean;
}>) {
  const show = shouldShowIneligibleCaption({
    eligibilityLineDisabledReasonCode,
    isEligible,
    isKnown,
    isWithdrawalEligibilityLoading,
  });
  if (!show) return null;

  const message = eligibilityLineDisabledReasonCode
    ? formatMessage(PM.paymentMessages.withdrawalNotEligibleReason, {
        reason: withdrawalDisabledReasonLabel(
          eligibilityLineDisabledReasonCode,
          formatMessage,
        ),
      })
    : formatMessage(PM.paymentMessages.withdrawalNotEligible);

  return (
    <Typography color='secondaryGrey' scale='p3'>
      {message}
    </Typography>
  );
}

function isGiftOrEngraving(productTypeName: string) {
  return productTypeName === 'gift' || productTypeName === 'engraving';
}

export type ReturnLineItemProps = {
  allOrderLines: OrderDetailsOrderLine[];
  imageLoader: ImageLoader;
  onDescriptionChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onToggle: (checked: boolean) => void;
  reasonOptions: SelectOption[];
  vm: ReturnLineItemViewModel;
};

export const ReturnLineItem: FC<ReturnLineItemProps> = ({
  allOrderLines,
  imageLoader,
  onDescriptionChange,
  onReasonChange,
  onToggle,
  reasonOptions,
  vm,
}) => {
  const { formatMessage } = useIntl();
  const {
    baseFieldId,
    channelSlug,
    eligibilityLine,
    fields,
    index,
    isEligible,
    isKnown,
    isLineDisabled,
    isMobileView,
    isSelected,
    isWithdrawalEligibilityLoading,
    languageCode,
    orderChannelSlug,
    orderLine,
    taxMessage,
    voucherType,
  } = vm;
  const { productName, thumbnail } = getWarrantyOrderLineDetails(
    orderLine,
    allOrderLines,
    formatMessage,
  );
  const productType = getProductType(orderLine?.variant?.product?.productType);
  const lineId = orderLine.id;
  const descriptionFieldId = `${baseFieldId}-description-${lineId}`;
  const reasonInputId = `${baseFieldId}-reason-${lineId}`;
  const returnReasonAria = formatMessage(PM.paymentMessages.chooseReturnReason);
  const returnDetailsAria = formatMessage(
    PM.paymentMessages.returnFlowAdditionalDetails,
  );
  const productTypeName =
    orderLine?.variant?.product.productType.name.toLowerCase() ?? '';
  const selectedReasonOption = fields.reason
    ? getSelectedReasonOption(fields.reason, reasonOptions)
    : null;

  const isGreyedOut = isKnown && !isEligible && !isWithdrawalEligibilityLoading;
  const isEngraving = productType === ProductType.Engraving;
  const showSku = !isEngraving;

  const shouldAllowToggle = !isLineDisabled;
  const handleToggle = (checked: boolean) => {
    if (!shouldAllowToggle) return;
    onToggle(checked);
  };

  const ineligibleCaption = buildIneligibleCaption({
    eligibilityLineDisabledReasonCode: eligibilityLine?.disabledReasonCode,
    formatMessage,
    isEligible,
    isKnown,
    isWithdrawalEligibilityLoading,
  });

  return (
    <RowStyles.ReturnOrderLineRow
      $greyedOut={isGreyedOut}
      alignItems='flex-start'
      data-testid={`OrderReturnViewLine-${index}`}
      justifyContent='space-between'
    >
      <RowStyles.ReturnOrderProductImageInfo
        alignItems='flex-start'
        flexWrap={isMobileView ? 'wrap' : 'nowrap'}
      >
        <Box paddingTop={1} style={{ flexShrink: 0 }}>
          <Checkbox
            aria-label={productName}
            borderColorVariant='black'
            checked={isSelected}
            data-test-id={`OrderReturnViewLineCheckbox-${lineId}`}
            disabled={isLineDisabled}
            onChange={(e) => handleToggle(e.target.checked)}
          />
        </Box>
        <S.ImageWrapper>
          {thumbnail ? (
            <Image
              alt={productName ?? ''}
              data-test-id={`OrderReturnViewLineImage-${index}`}
              fill
              loader={imageLoader}
              src={thumbnail.url}
              style={{
                objectFit: 'contain',
              }}
            />
          ) : null}
        </S.ImageWrapper>
        <Flex
          alignItems='flex-start'
          flexWrap='wrap'
          gridGap={3}
          style={{ minWidth: 0 }}
          width='100%'
        >
          <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
            <S.StyledGrid>
              <ProductName
                channel={channelSlug}
                id={orderLine?.variant?.product?.id}
                index={index}
                languageCode={languageCode}
                productName={productName}
                productType={orderLine?.variant?.product?.productType?.slug}
              />
              {showSku ? (
                <Typography color='secondaryGrey' scale='p3'>
                  {orderLine.variant?.sku}
                </Typography>
              ) : null}
              {ineligibleCaption}
            </S.StyledGrid>
          </Box>

          {isSelected && !isLineDisabled ? (
            <Box marginTop={2} style={{ flex: '0 0 100%', minWidth: 0 }}>
              <Box
                style={{
                  width: isMobileView ? '100%' : '240px',
                }}
              >
                <Select
                  aria-label={returnReasonAria}
                  borderColorVariant='lightGray'
                  colorVariant='grey'
                  data-test-id={`OrderReturnViewReasonSelect-${lineId}`}
                  inputId={reasonInputId}
                  isSearchable={false}
                  name={`return-reason-${lineId}`}
                  onChange={(opt) => onReasonChange(getSelectOptionValue(opt))}
                  options={reasonOptions}
                  placeholder={formatMessage(
                    PM.paymentMessages.chooseReturnReason,
                  )}
                  value={selectedReasonOption}
                  variant='default'
                />
              </Box>
              <Box
                marginTop={2}
                style={{
                  width: isMobileView ? '100%' : '400px',
                }}
              >
                <TextArea
                  aria-label={returnDetailsAria}
                  borderColorVariant='lightGray'
                  colorVariant='grey'
                  data-test-id={`OrderReturnViewDescription-${lineId}`}
                  id={descriptionFieldId}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  placeholder={formatMessage(
                    PM.paymentMessages.returnDescriptionPlaceholder,
                  )}
                  rows={5}
                  value={fields.description}
                />
              </Box>
            </Box>
          ) : null}
        </Flex>
      </RowStyles.ReturnOrderProductImageInfo>
      <RowStyles.ReturnPriceWrap>
        {isGiftOrEngraving(productTypeName) ? null : (
          <>
            <TaxedPrice
              channel={orderChannelSlug}
              data-test-id={`OrderReturnViewLinePrice-${index}`}
              displayZero={voucherType !== VOUCHER_TYPE.PERCENTAGE}
              justifyContent='right'
              price={getOrderLineUnitPriceWithoutDiscount(
                orderLine,
                voucherType,
              )}
            />
            {voucherType !== VOUCHER_TYPE.PERCENTAGE && taxMessage ? (
              <Typography
                color='secondaryGrey'
                data-test-id={`OrderReturnViewLineVat-${index}`}
                scale='p3'
                textAlign='right'
              >
                {formatMessage(taxMessage)}
              </Typography>
            ) : null}
          </>
        )}
      </RowStyles.ReturnPriceWrap>
    </RowStyles.ReturnOrderLineRow>
  );
};
